import { SyntheticEvent, useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Autocomplete as MuiAutocomplete,
} from '@mui/material';

import { CoreBox } from '@youscience/core';

import {
  RecommenderDocument,
  RolesDocument,
  SubjectArea,
  SubjectAreaMapping,
} from '@youscience/commonapp-service-common';

import AddIcon from '@images/plus-button.svg?react';

import ActionButton from '@components/ActionButton';

import { StyledTeacherButtonGroup } from './AddTeacher.styles';

type AddTeacherProps = {
  teacherRoles: RolesDocument[];
  recommenders: RecommenderDocument[];
  editTeacher?: RecommenderDocument;
  onAssignTeacher: (recommenderId: number) => Promise<void>;
  onEditTeacher?: (recommenderId: number, subject: string) => Promise<void>;
  onAddAndAssignTeacher: (userId: string, subject: SubjectArea) => Promise<void>;
  onCancel: () => void;
};

const ID_TAGS = {
  role: 'role',
  recommender: 'recommender',
};

const EMPTY_TEACHER = { userId: '', subject: '' };
const EMPTY_RECOMMENDER = { recommenderId: '', subject: '' };

interface AutocompleteOption {
  key: string;
  value: string;
  label: string;
}

export const AddTeacher = ({
  teacherRoles,
  recommenders,
  editTeacher,
  onAssignTeacher,
  onAddAndAssignTeacher,
  onCancel,
}: AddTeacherProps) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [subjectNeeded, setSubjectNeeded] = useState<boolean>(false);
  const [teacherOptions, setTeacherOptions] = useState<AutocompleteOption[]>([]);
  const [selectedTeacher, setSelectedTeacher] = useState<AutocompleteOption | null>(null);
  const [teacherData, setTeacherData] = useState<{ userId: string; subject: string }>(EMPTY_TEACHER);
  const [recommender, setRecommender] = useState<{ recommenderId: string; subject: string }>(EMPTY_RECOMMENDER);

  useEffect(() => {
    setTeacherData(EMPTY_TEACHER);
    setRecommender(EMPTY_RECOMMENDER);
    setSelectedTeacher(null);
    setSubjectNeeded(false);

    if (editTeacher) {
      const teacherItem = {
        key: `${editTeacher.recommenderId}`,
        value: `${ID_TAGS.recommender}:${editTeacher.recommenderId}`,
        label: `${editTeacher.firstName} ${editTeacher.lastName}`,
      };
      setTeacherOptions([teacherItem]);
      handleSelectTeacher(teacherItem);
      setRecommender({ recommenderId: editTeacher.recommenderId.toString(), subject: editTeacher.subject as string });
      setTeacherData({ userId: '', subject: editTeacher?.subject ?? '' });
      setSubjectNeeded(true);
    } else {
      const recommenderOptions = recommenders
        ?.map((recommender) => ({
          key: `${recommender.recommenderId}`,
          value: `${ID_TAGS.recommender}:${recommender.recommenderId}`,
          label: `${recommender.firstName} ${recommender.lastName} - ${recommender.subject as string}`,
        }))
        ?.sort((a, b) => a.label.localeCompare(b.label));

      const roleOptions = teacherRoles
        ?.map((role) => ({
          key: role.userId,
          value: `${ID_TAGS.role}:${role.userId}`,
          label: `${role.firstName} ${role.lastName}`,
        }))
        ?.sort((a, b) => a.label.localeCompare(b.label));

      setTeacherOptions([...recommenderOptions, ...roleOptions]);
    }
  }, [recommenders, teacherRoles, editTeacher]);

  const handleUpdateTeacher = (event: SyntheticEvent, teacherOption: AutocompleteOption | null) => {
    if (teacherOption?.value.startsWith(ID_TAGS.role)) {
      const userId = teacherOption?.value.split(':')[1];

      setTeacherData((prev) => {
        return { ...prev, userId: userId.toString(), subject: '' };
      });
      setRecommender(EMPTY_RECOMMENDER);
      setSubjectNeeded(true);
    } else {
      const recommenderId = Number(teacherOption?.value.split(':')[1]);
      const recommender = recommenders.find((recommender) => recommender.recommenderId === recommenderId);

      setRecommender({ recommenderId: recommenderId.toString(), subject: recommender?.subject ?? '' });
      setTeacherData({ userId: '', subject: recommender?.subject ?? '' });
      setSubjectNeeded(false);
    }

    handleSelectTeacher(teacherOption);
  };

  const handleUpdateSubject = (event: SelectChangeEvent<unknown>) => {
    setTeacherData((prev) => ({ ...prev, subject: event.target.value as string }));
  };

  const handleAddTeacher = async () => {
    setIsSaving(true);

    if (recommender.recommenderId && recommender.subject) {
      await onAssignTeacher(+recommender.recommenderId).finally(() => setIsSaving(false));
    } else if (teacherData.userId && teacherData.subject) {
      await onAddAndAssignTeacher(teacherData.userId, teacherData.subject as SubjectArea).finally(() =>
        setIsSaving(false),
      );
    }
  };

  const handleSelectTeacher = (teacherItem: AutocompleteOption | null) => {
    if (!teacherItem) return;

    const trimmedLabel = teacherItem?.label.split(' - ')[0].trim() ?? '';
    setSelectedTeacher({ ...teacherItem, label: trimmedLabel });
  };

  const isAddButtonDisabled = !(
    (recommender.recommenderId && recommender.subject) ||
    (teacherData.userId && teacherData.subject)
  );

  const sortedSubjects = Object.values(SubjectAreaMapping).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });

  return (
    <CoreBox style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'auto auto' }}>
      <FormControl size="small" sx={{ width: '400px' }}>
        <MuiAutocomplete
          data-testid="teacher-selection"
          size="small"
          options={teacherOptions || []}
          value={selectedTeacher}
          isOptionEqualToValue={(option, teacher) => option.value === teacher.value}
          onChange={handleUpdateTeacher}
          renderInput={(params) => <TextField {...params} label="Select your teacher" />}
          clearIcon={null}
          noOptionsText={''}
          disabled={isSaving || Boolean(editTeacher)}
        />
      </FormControl>

      <FormControl size="small" sx={{ width: '200px' }}>
        <InputLabel>Subject</InputLabel>
        <Select
          data-testid="subject"
          label="Subject"
          value={teacherData?.subject ?? ''}
          onChange={handleUpdateSubject}
          disabled={!subjectNeeded || isSaving}
        >
          {sortedSubjects.map((subject) => (
            <MenuItem key={`subject${subject}`} value={subject}>
              {subject}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <StyledTeacherButtonGroup sx={{ gridColumn: '1 / span 2', justifyContent: 'end' }}>
        <ActionButton onClick={onCancel} variant="outlined" disabled={isSaving}>
          Cancel
        </ActionButton>

        <ActionButton
          onClick={handleAddTeacher}
          busyText="Adding"
          disabled={isAddButtonDisabled}
          startIcon={<AddIcon />}
        >
          Add
        </ActionButton>
      </StyledTeacherButtonGroup>
    </CoreBox>
  );
};
